import type { CSSProperties, ReactNode } from 'react'
import './hero-illustrations.css'

type HeroVariant =
  | 'crawl'
  | 'pent'
  | 'vis'
  | 'aeo'
  | 'gen'
  | 'valid'
  | 'robots'
  | 'og'
  | 'badge'
  | 'track'
  | 'cite'
  | 'vs'
  | 'reddit'
  | 'fresh'
  | 'yt'

/** A translucent edge and engraved spine, behind the existing document. */
function Folio({
  x,
  y,
  width,
  height,
}: {
  x: number
  y: number
  width: number
  height: number
}) {
  return (
    <g className='ha-folio'>
      <path
        d={`M${x} ${y}l-10 10v${height}h${width}l10-10H${x}Z`}
        className='ha-blue-plane'
      />
      <path
        d={`M${x - 10} ${y + 10}l10-10h${width}l-10 10Z`}
        className='ha-gold-plane'
      />
      {[3, 6, 9].map(offset => (
        <path
          key={offset}
          d={`M${x - offset} ${y + offset + 8}v${height - 8}h${width - 8}`}
          className='ha-engraving'
        />
      ))}
      <path
        d={`M${x + width - 34} ${y - 5}h39v39`}
        className='ha-pink-line ha-trace'
        pathLength='1'
      />
    </g>
  )
}

/** Small turned discs keep the original signal positions and counts intact. */
function SignalDisc({ x, y, r = 14 }: { x: number; y: number; r?: number }) {
  return (
    <g>
      <path
        d={`M${x - r} ${y}a${r} ${r} 0 0 0 ${r * 2} 0v5a${r} ${r} 0 0 1 ${-r * 2} 0Z`}
        className='ha-blue-plane'
      />
      <circle cx={x} cy={y} r={r} className='ha-paper-plane' />
      <circle cx={x} cy={y} r={r + 3} className='ha-engraving' />
    </g>
  )
}

function Backdrop({ variant }: { variant: HeroVariant }) {
  switch (variant) {
    case 'crawl':
      return (
        <g>
          <path d='M48 46h264M48 94h264M48 142h220' className='ha-engraving' />
          {Array.from({ length: 17 }, (_, i) => (
            <SignalDisc
              key={i}
              x={70 + (i % 6) * 44}
              y={46 + Math.floor(i / 6) * 48}
            />
          ))}
          <path
            d='M52 178h256'
            className='ha-pink-line ha-trace'
            pathLength='1'
          />
          <path d='M52 174v8m256-8v8' className='ha-engraving' />
        </g>
      )
    case 'pent':
      return (
        <g>
          <path
            d='M128 30a78 78 0 0 0 0 156v-30a48 48 0 0 1 0-96Z'
            className='ha-blue-plane'
          />
          <path
            d='M128 30a78 78 0 0 1 74 54l-29 9a48 48 0 0 0-45-33Z'
            className='ha-gold-plane'
          />
          <path
            d='M202 84a78 78 0 0 1-74 102v-30a48 48 0 0 0 45-63Z'
            className='ha-pink-plane'
          />
          {[54, 60, 66, 72].map(r => (
            <circle key={r} cx='128' cy='108' r={r} className='ha-engraving' />
          ))}
          <path
            d='M249 107a41 41 0 0 1 82 0'
            className='ha-blue-line ha-trace'
            pathLength='1'
          />
          <circle cx='290' cy='98' r='34' className='ha-paper-plane' />
        </g>
      )
    case 'vis':
      return (
        <g>
          <path
            d='M46 140a104 104 0 0 1 208 0h-30a74 74 0 0 0-148 0Z'
            className='ha-blue-plane'
          />
          {[102, 98, 82, 78].map(r => (
            <path
              key={r}
              d={`M${150 - r} 140a${r} ${r} 0 0 1 ${2 * r} 0`}
              className='ha-engraving'
            />
          ))}
          <path d='M60 159h180l18 18H42Z' className='ha-gold-plane' />
          <path d='M42 181h216M60 185h180' className='ha-engraving' />
          <path
            d='M274 184h68'
            className='ha-pink-line ha-trace'
            pathLength='1'
          />
        </g>
      )
    case 'aeo':
      return <Folio x={28} y={14} width={196} height={192} />
    case 'gen':
      return <Folio x={36} y={18} width={208} height={184} />
    case 'valid':
      return (
        <g>
          <path
            d='M82 158a58 58 0 0 1 0-116v24a34 34 0 0 0 0 68Z'
            className='ha-pink-plane'
          />
          <Folio x={100} y={14} width={160} height={176} />
          <path d='M30 100h52m-26-26v52' className='ha-engraving' />
        </g>
      )
    case 'robots':
      return <Folio x={20} y={14} width={320} height={176} />
    case 'og':
      return <Folio x={26} y={16} width={190} height={186} />
    case 'badge':
      return <Folio x={32} y={26} width={296} height={156} />
    case 'track':
      return (
        <g>
          {[38, 74, 110, 146, 182].map(y => (
            <path
              key={y}
              d={`M108 ${y + 7}h200l8-8M108 ${y + 10}h200`}
              className='ha-engraving'
            />
          ))}
          <path
            d='M324 30v160'
            className='ha-blue-line ha-trace'
            pathLength='1'
          />
          {[38, 74, 110, 146, 182].map(y => (
            <path key={y} d={`M320 ${y}h8`} className='ha-engraving' />
          ))}
        </g>
      )
    case 'cite':
      return (
        <g>
          <Folio x={164} y={10} width={32} height={40} />
          {[54, 138, 222, 306].map(x => (
            <SignalDisc key={x} x={x} y={124} r={26} />
          ))}
          <path
            d='M54 88c0-25 252-25 252 0'
            className='ha-blue-line ha-trace'
            pathLength='1'
          />
        </g>
      )
    case 'vs':
      return (
        <g>
          <path d='M146 30v158M214 30v158' className='ha-engraving' />
          {[44, 70, 96, 122, 148, 174].map(y => (
            <path
              key={y}
              d={`M20 ${y + 9}h120m80 0h120`}
              className='ha-engraving'
            />
          ))}
          <path
            d='M22 27h118M220 27h118'
            className='ha-blue-line ha-trace'
            pathLength='1'
          />
        </g>
      )
    case 'reddit':
      return (
        <g>
          <path
            d='M46 52L84 96 62 138 130 160 150 118 176 48 262 42 318 72 286 128 300 160 218 110 236 92 120 60 84 96'
            className='ha-engraving'
          />
          <path
            d='M120 60Q170 36 236 92T300 160'
            className='ha-pink-line ha-trace'
            pathLength='1'
          />
          {[
            [46, 52],
            [84, 96],
            [62, 138],
            [120, 60],
            [150, 118],
            [176, 48],
            [198, 150],
            [236, 92],
            [262, 42],
            [286, 128],
            [318, 72],
            [300, 160],
            [130, 160],
            [218, 110],
          ].map(([x, y]) => (
            <SignalDisc key={`${x}-${y}`} x={x} y={y} r={12} />
          ))}
        </g>
      )
    case 'fresh':
      return (
        <g>
          <path d='M228 66h96v84h-96Z' className='ha-paper-plane' />
          <path d='M228 66h96l10-9h-96Z' className='ha-gold-plane' />
          <path d='M324 66l10-9v84l-10 9Z' className='ha-blue-plane' />
          {[24, 30, 36, 42].map(r => (
            <path
              key={r}
              d={`M${72 - r} 78a${r} ${r} 0 0 1 ${r * 2} 0`}
              className='ha-engraving'
            />
          ))}
          <path
            d='M30 154h304'
            className='ha-blue-line ha-trace'
            pathLength='1'
          />
        </g>
      )
    case 'yt':
      return (
        <g>
          {[
            [24, 20],
            [134, 20],
            [244, 20],
            [24, 92],
            [134, 92],
            [244, 92],
          ].map(([x, y]) => (
            <Folio key={`${x}-${y}`} x={x} y={y} width={92} height={54} />
          ))}
        </g>
      )
  }
}

/** Opt-in art skin: the diagram's labels, marks and data remain its children. */
export default function HeroIllustration({
  variant,
  children,
}: {
  variant: HeroVariant
  children: ReactNode
}) {
  const id = `pm-hero-${variant}`
  const style = {
    '--ha-blue-fill': `url(#${id}-blue)`,
    '--ha-pink-fill': `url(#${id}-pink)`,
    '--ha-gold-fill': `url(#${id}-gold)`,
    '--ha-paper-fill': `url(#${id}-paper)`,
  } as CSSProperties

  return (
    <svg
      className={`glyph g-${variant} pm-hero-art`}
      viewBox='-16 -12 392 256'
      aria-hidden='true'
      focusable='false'
      style={style}
    >
      <defs>
        <linearGradient id={`${id}-blue`} x1='0' y1='1' x2='1' y2='0'>
          <stop stopColor='var(--ink)' stopOpacity='.85' />
          <stop offset='.36' stopColor='var(--blue)' stopOpacity='.88' />
          <stop offset='1' stopColor='var(--blue-light)' stopOpacity='.22' />
        </linearGradient>
        <linearGradient id={`${id}-pink`} x1='0' y1='1' x2='1' y2='0'>
          <stop stopColor='var(--pink)' stopOpacity='.9' />
          <stop offset='1' stopColor='var(--pink)' stopOpacity='.15' />
        </linearGradient>
        <linearGradient id={`${id}-gold`} x1='0' y1='0' x2='1' y2='1'>
          <stop stopColor='var(--yellow)' stopOpacity='.85' />
          <stop offset='1' stopColor='var(--yellow)' stopOpacity='.12' />
        </linearGradient>
        <linearGradient id={`${id}-paper`} x1='0' y1='0' x2='1' y2='1'>
          <stop stopColor='var(--paper)' stopOpacity='.96' />
          <stop offset='.65' stopColor='var(--paper)' stopOpacity='.88' />
          <stop offset='1' stopColor='var(--blue-light)' stopOpacity='.18' />
        </linearGradient>
      </defs>
      <g className='ha-backdrop'>
        <Backdrop variant={variant} />
      </g>
      {children}
    </svg>
  )
}
